import type { GtmSupport } from "@gtm-support/vue-gtm";

const OSANO_SRC = "https://cmp.osano.com/AzZMxHTbQDOQD8c1J/14b5e47f-2a58-43bd-83ad-36520573f0d8/osano.js";
const TWITTER_PIXEL_ID = "ofedz";
const TWITTER_UWT_SRC = "https://static.ads-twitter.com/uwt.js";

type OsanoConsentValue = "ACCEPT" | "DENY";

export type OsanoConsent = Partial<Record<string, OsanoConsentValue>>;

// Trackers run by default on first load (opt-out model). An explicit Osano
// consent object — saved by the user now or stored from a previous visit —
// overrides these defaults per category.
let analyticsConsent = true;
let marketingConsent = true;
let twitterPixelConfigured = false;
let gtmInstance: GtmSupport | undefined;

function isAccepted(value: OsanoConsentValue | undefined): boolean {
  return value === "ACCEPT";
}

export function isAnalyticsConsentGranted(): boolean {
  return analyticsConsent;
}

export function isMarketingConsentGranted(): boolean {
  return marketingConsent;
}

export function registerGtm(gtm: GtmSupport): void {
  gtmInstance = gtm;
  syncGtmConsent();
}

function injectScript(src: string): void {
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.defer = true;
  script.src = src;
  document.head.appendChild(script);
}

function createTwqStub(): Window["twq"] {
  const twq = function (...args: unknown[]) {
    // Gate inside the stub instead of swapping window.twq: uwt.js attaches
    // its runtime (exe) to this object and replays queue from it, so replacing
    // it would permanently detach the pixel.
    if (!marketingConsent) {
      return;
    }
    if (twq.exe) {
      twq.exe(...args);
    } else {
      twq.queue.push(args);
    }
  } as Window["twq"];
  twq.version = "1.1";
  twq.queue = [];
  return twq;
}

function initTwitterBootstrap(): void {
  if (!window.twq) {
    window.twq = createTwqStub();
  }

  injectScript(TWITTER_UWT_SRC);
}

function syncTwitterPixel(): void {
  if (twitterPixelConfigured || !marketingConsent || !window.twq) {
    return;
  }

  window.twq("config", TWITTER_PIXEL_ID);
  twitterPixelConfigured = true;
}

function syncGtmConsent(): void {
  gtmInstance?.enable(analyticsConsent);
}

export function applyConsent(consent: OsanoConsent | undefined): void {
  // undefined means Osano initialized without a stored decision — the user
  // has not actioned the banner yet, so keep the default-allow behavior.
  analyticsConsent = consent ? isAccepted(consent.ANALYTICS) : true;
  marketingConsent = consent ? isAccepted(consent.MARKETING) : true;

  syncGtmConsent();
  syncTwitterPixel();
}

export function applyStoredConsent(): void {
  if (window.Osano?.cm?.getConsent) {
    applyConsent(window.Osano.cm.getConsent());
  }
}

function initOsanoPreload(): void {
  if (!window.Osano) {
    const queue: unknown[][] = [];
    const osano = function (...args: unknown[]) {
      queue.push(args);
    } as NonNullable<Window["Osano"]>;
    osano.data = queue;
    window.Osano = osano;
  }

  window.Osano("onInitialized", applyConsent);
  window.Osano("onConsentSaved", applyConsent);
}

export function initConsent(): void {
  initOsanoPreload();
  injectScript(OSANO_SRC);
  initTwitterBootstrap();
  syncTwitterPixel();
}
